import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { AnalyticService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { UpdateView } from '@common/views';
import { Role } from '@models/account/role.model';
import { Permission } from '@models/permissions/permission.model';
import * as Sentry from '@sentry/browser';
import { Module, ModuleFeature } from '@settings/interfaces/module-permissions.interface';
import {
    EmployeeAccessLevels,
    Levels,
    Profile,
    ProfileSegment,
} from '@settings/interfaces/profile-permissions.interface';
import { ModuleFeaturePermissions } from '@settings/meta/module-feature-permissions.meta';
import { ProfilePermissions } from '@settings/meta/profile-permissions.meta';
import { RequiredPermissions } from '@settings/meta/required-permissions.meta';
import isEqual from 'lodash-es/isEqual';

@Component({
    templateUrl: './role-update.template.html',
    styleUrls: ['./role-update.style.scss'],
})
export class RoleUpdateView extends UpdateView implements OnInit {
    role: Role;
    permissions: Permission[];
    moduleFeaturePermissions: Module[] = [];
    profilePermissions: Profile[] = [];
    employeeAccessLevel = 'direct reports';
    isLoading = false;

    assignableRoles: Role[] = [];
    originalAssignableRolesIds: number[] = [];
    isPerformanceModuleSplit = false;

    constructor(
        public auth: AuthService,
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        private featureService: FeatureService,
        private analyticsService: AnalyticService
    ) {
        super();
        this.role = this.route.snapshot.data.models.get('role');
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;

        this.moduleFeaturePermissions = await this.filterModules(ModuleFeaturePermissions);
        this.isPerformanceModuleSplit = await this.featureService.has(FeatureFlag.performanceModuleSplit);
        this.profilePermissions = await this.filterPermissionsByFeatureFlag(
            JSON.parse(JSON.stringify(ProfilePermissions))
        );

        // These steps have to happen *after* all the profile filtering above.
        this.injectModulePermissionFromRole(this.role.permissions);
        this.injectProfilePermissionFromRole(this.role.permissions);
        this.injectAccessLevelFromRole(this.role.permissions);
        this.permissions = this.auth.role.permissions;

        const [roles] = await Role.where('company_id', this.auth.company.id).where('assignableBy', this.role.id).all();

        this.assignableRoles = roles;
        this.originalAssignableRolesIds = roles.map((role: Role) => role.id);

        this.isLoading = false;
    }

    async onSave(): Promise<void> {
        if (this.role.isImmutable) {
            this.router.navigate(['/settings', 'roles', this.role.id]);
            return;
        }

        const roleAssignmentUnchanged = isEqual(
            this.assignableRoles.map((role: Role) => role.id).sort(),
            this.originalAssignableRolesIds.sort()
        );

        this.isLoading = true;

        this.role.permissionIds = this.preparePermissionIds();
        this.role.assignableRolesIds = this.assignableRoles.map((role: Role) => role.id);

        try {
            await this.role.save();

            this.analyticsService.trackEvent(AnalyticEvents.RoleUpdated);

            if (!roleAssignmentUnchanged) {
                this.analyticsService.trackEvent(AnalyticEvents.RoleAssignmentControlChanged);
            }

            this.notify.success('Role updated');
        } catch (err) {
            this.notify.error(err.message || 'Could not save role.');
        } finally {
            this.router.navigate(['/settings', 'roles', this.role.id]);
        }
    }

    /**
     * Take the cumulative permissions from each stage (module, profile, required)
     * and return a unique array of their corresponding permission model ids
     */
    preparePermissionIds(): number[] {
        const modulesPermissions: string[] = this.extractPermissionsFromModule(this.moduleFeaturePermissions);
        const profilePermissions: string[] = this.extractPermissionsFromProfile(this.profilePermissions);
        const requiredPermissions: string[] = this.extractPermissionsFromRequired(RequiredPermissions);
        const accessLevel: string[] = this.extractPermissionFromAccessLevel();
        const uniquePermissions: Set<string> = new Set<string>();

        modulesPermissions.forEach((permission) => uniquePermissions.add(permission));

        if (this.employeeAccessLevel !== EmployeeAccessLevels.onlySelf) {
            profilePermissions.forEach((permission) => uniquePermissions.add(permission));
        }

        requiredPermissions.forEach((permission) => uniquePermissions.add(permission));
        accessLevel.forEach((permission) => uniquePermissions.add(permission));
        const uniquePermissionIds: Set<number> = new Set<number>();

        for (const permission of Array.from(uniquePermissions)) {
            const foundPermission = this.permissions.find(
                (allowedPermissions) => allowedPermissions.name === permission
            );
            if (foundPermission) {
                uniquePermissionIds.add(foundPermission.id);
            } else {
                Sentry.captureException(new Error('Permission missing on role update: ' + permission));
                console.warn('Missing permission while attempting to assign', permission);
            }
        }

        return Array.from(uniquePermissionIds);
    }

    /**
     * Find the correct access level permission
     */
    extractPermissionFromAccessLevel(): string[] {
        switch (this.employeeAccessLevel) {
            case EmployeeAccessLevels.all:
                return ['manageAll', 'manageDirectReports', 'manageDirectAndIndirectReports', 'manageSelf'];
            case EmployeeAccessLevels.directReports:
                return ['manageDirectReports'];
            case EmployeeAccessLevels.reports:
                return ['manageDirectAndIndirectReports'];
            case EmployeeAccessLevels.onlySelf:
            default:
                return ['manageSelf'];
        }
    }

    /**
     * Find the correct access level permission
     */
    injectAccessLevelFromRole(permissions: Permission[]): void {
        const includesPermission = (permission: string) =>
            permissions.map((permissionModel) => permissionModel.name).includes(permission);

        if (includesPermission('manageAll')) {
            this.employeeAccessLevel = EmployeeAccessLevels.all;
            return;
        }
        if (includesPermission('manageSelf')) {
            this.employeeAccessLevel = EmployeeAccessLevels.onlySelf;
        }
        if (includesPermission('manageDirectReports')) {
            this.employeeAccessLevel = EmployeeAccessLevels.directReports;
        }
        if (includesPermission('manageDirectAndIndirectReports')) {
            this.employeeAccessLevel = EmployeeAccessLevels.reports;
        }
    }

    /**
     * Loop the toggled module features and uniquely add them to an array of permission ids
     */
    private extractPermissionsFromModule(moduleFeaturePermissions: Module[]): string[] {
        const permissionStrings: Set<string> = new Set<string>();
        for (const moduleFeaturePermission of moduleFeaturePermissions) {
            if (moduleFeaturePermission.toggled) {
                moduleFeaturePermission.permissions.forEach((permission) => permissionStrings.add(permission));
            }

            // We reject features hidden by "feature flags" otherwise we unintentionally enable modules for the role
            // See https://gethumi.atlassian.net/browse/SQE-5271
            // This condition should only run when the feature flag is active
            for (const feature of moduleFeaturePermission.features.filter((featureItem) =>
                this.isPerformanceModuleSplit ? !featureItem.hideIfHasFeatureFlag : true
            )) {
                if (feature.toggled) {
                    feature.permissions.forEach((permission) => permissionStrings.add(permission));
                }
            }
        }
        return Array.from(permissionStrings);
    }

    /**
     * Loop the existing permissions and update the module feature flags
     */
    private injectModulePermissionFromRole(permissions: Permission[]): void {
        for (const moduleFeaturePermission of this.moduleFeaturePermissions) {
            const requiredModulePermissions = moduleFeaturePermission.permissions;
            if (requiredModulePermissions) {
                const hasModulePermissions = requiredModulePermissions.every((permission) => {
                    return permissions.map((permissionModel) => permissionModel.name).includes(permission);
                });
                if (hasModulePermissions) {
                    moduleFeaturePermission.toggled = true;
                } else {
                    moduleFeaturePermission.toggled = false;
                }
            }

            for (const feature of moduleFeaturePermission.features) {
                const requiredPermissions = feature.permissions;
                const hasPermissions = requiredPermissions.every((permission) => {
                    return permissions.map((permissionModel) => permissionModel.name).includes(permission);
                });
                if (hasPermissions) {
                    feature.toggled = true;
                } else {
                    feature.toggled = false;
                }
            }
        }
    }

    /**
     * Loop the profile permissions and uniquely add them to an array if permission ids
     */
    private extractPermissionsFromProfile(profilePermissions: Profile[]): string[] {
        const permissionStrings: Set<string> = new Set<string>();
        for (const profileTab of profilePermissions) {
            // We reject segments hidden by "feature flags" otherwise we unintentionally enable modules for the role
            // See https://gethumi.atlassian.net/browse/SQE-5271
            // This condition should only run when the feature flag is active
            for (const segment of profileTab.segments.filter((segment) =>
                this.isPerformanceModuleSplit ? !segment.hideIfHasFeatureFlag : true
            )) {
                if (segment.level) {
                    switch (segment.level) {
                        case 'edit':
                            segment.edit.forEach((permission) => permissionStrings.add(permission));
                            segment.view.forEach((permission) => permissionStrings.add(permission));
                            break;
                        case 'view':
                            segment.view.forEach((permission) => permissionStrings.add(permission));
                            break;
                    }
                }
            }
        }
        return Array.from(permissionStrings);
    }

    /**
     * Loop the existing permissions and update the profile permission flags
     */
    private injectProfilePermissionFromRole(permissions: Permission[]): void {
        for (const profileTab of this.profilePermissions) {
            for (const segment of profileTab.segments) {
                // view
                const requiredViewPermissions = segment.view;
                const hasViewPermissions = requiredViewPermissions.every((permission) => {
                    return permissions.map((permissionModel) => permissionModel.name).includes(permission);
                });
                if (hasViewPermissions) {
                    segment.level = Levels.view;
                }
                // edit
                const requiredEditPermissions = [...segment.edit, ...segment.view];
                const hasEditPermissions = requiredEditPermissions.every((permission) => {
                    return permissions.map((permissionModel) => permissionModel.name).includes(permission);
                });
                if (hasEditPermissions) {
                    segment.level = Levels.edit;
                }
            }
        }
    }

    /**
     * Loop the required permissions and uniquely add them to an array of permission ids
     */
    private extractPermissionsFromRequired(requiredPermissions: string[]): string[] {
        return requiredPermissions;
    }

    private async filterModules(modules: Module[]): Promise<Module[]> {
        let filtered: Module[] = [];

        /**
         * Filter modules by feature flag
         */
        for (const item of modules) {
            if (item.showIfHasFeatureFlag && !(await this.featureService.has(item.showIfHasFeatureFlag))) {
                continue;
            }
            if (item.hideIfHasFeatureFlag && (await this.featureService.has(item.hideIfHasFeatureFlag))) {
                continue;
            }
            this.filterModuleFeaturesByFeatureFlag(item);

            filtered.push(item);
        }

        /**
         * Sometimes we want to hide permissions based on module being enabled or not, this will fiter out any modules
         * in the permissions ui if the `showIfModuleEnabled` flag is set, and the company doesnt have that module.
         */
        filtered = filtered.filter((module: Module) =>
            module.showIfModuleEnabled
                ? this.auth.company.modules.findIndex((m) => m.name === module.showIfModuleEnabled) > -1
                : true
        );

        return filtered;
    }

    /**
     * Filter features per module based on feature flag
     */
    private async filterModuleFeaturesByFeatureFlag(module: Module) {
        const moduleFeatures: ModuleFeature[] = [];

        for (const feature of module.features) {
            if (feature.showIfHasFeatureFlag && !(await this.featureService.has(feature.showIfHasFeatureFlag))) {
                continue;
            }
            if (feature.hideIfHasFeatureFlag && (await this.featureService.has(feature.hideIfHasFeatureFlag))) {
                continue;
            }

            moduleFeatures.push(feature);
        }
        module.features = [...moduleFeatures];
    }

    private async filterPermissionsByFeatureFlag(permissions: Profile[]): Promise<Profile[]> {
        const filtered: Profile[] = [];

        for (const item of permissions) {
            if (item.showIfHasFeatureFlag && !(await this.featureService.has(item.showIfHasFeatureFlag))) {
                continue;
            }
            if (item.hideIfHasFeatureFlag && (await this.featureService.has(item.hideIfHasFeatureFlag))) {
                continue;
            }

            item.segments = [...(await this.filterProfileSegmentsByFeatureFlag(item.segments))];

            filtered.push(item);
        }

        return filtered;
    }

    private async filterProfileSegmentsByFeatureFlag(profileSegments: ProfileSegment[]): Promise<ProfileSegment[]> {
        const filteredProfileSegments: ProfileSegment[] = [];

        for (const profileSegment of profileSegments) {
            if (
                profileSegment.showIfHasFeatureFlag &&
                !(await this.featureService.has(profileSegment.showIfHasFeatureFlag))
            ) {
                continue;
            }
            if (
                profileSegment.hideIfHasFeatureFlag &&
                (await this.featureService.has(profileSegment.hideIfHasFeatureFlag))
            ) {
                continue;
            }

            filteredProfileSegments.push(profileSegment);
        }

        return filteredProfileSegments;
    }
}
